/**
 * AuthField - labelled text input for the auth screens.
 *
 * These use a native `<input>` instead of `components/ui/input`: that component
 * applies `className` to its wrapper `<span>`, while the control's height and
 * font-size live on an inner class that callers cannot override, so the 46px
 * auth field is unreachable through it.
 *
 * The label is always visible. A placeholder-only field loses its name as soon
 * as the user types, and these are the fields people mistype most.
 */
import { cn } from "../../lib/utils";
import { AUTH_REVEAL_FIELDS, type AuthRevealStep, authRevealClass } from "./authReveal";

export type AuthFieldProps = Omit<React.ComponentPropsWithRef<"input">, "className" | "id"> & {
  /** Required: ties the visible label to the control. */
  id: string;
  label: string;
  /**
   * `"code"` is the wide-tracked, centred treatment for short one-time codes;
   * `"mono"` is monospaced at normal width, for longer machine strings (recovery
   * codes) that the wide tracking would push past the edge of the field.
   */
  variant?: "text" | "code" | "mono";
  /** Position in the entrance stagger; defaults to the fields' step. */
  revealStep?: AuthRevealStep;
  /** Extra classes for the wrapper, not the control. */
  className?: string;
};

export function AuthField({
  id,
  label,
  variant = "text",
  revealStep = AUTH_REVEAL_FIELDS,
  className,
  ...inputProps
}: AuthFieldProps) {
  return (
    <div className={cn(authRevealClass(revealStep), className)}>
      <label className="auth-label" htmlFor={id}>
        {label}
      </label>
      <input
        {...inputProps}
        id={id}
        className={cn(
          "auth-input",
          variant === "code" && "auth-input--code",
          variant === "mono" && "auth-input--mono",
        )}
      />
    </div>
  );
}

/** Groups `AuthField`s with the design's vertical rhythm. */
export function AuthFields(props: { children: React.ReactNode }) {
  return <div className="auth-fields">{props.children}</div>;
}
